import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, ArrowRight, Mail, PartyPopper, Calendar } from "lucide-react";

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const purchaseType = searchParams.get("type"); // v2 flow indicator
  const [status, setStatus] = useState("polling");
  const [planInfo, setPlanInfo] = useState(null);
  const hasPolled = useRef(false);

  useEffect(() => {
    if (!sessionId || hasPolled.current) return;
    hasPolled.current = true;

    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = 2000;
    const isV2 = !!purchaseType;

    const poll = async () => {
      try {
        const path = isV2 ? `/payments/v2/status/${sessionId}` : `/payments/status/${sessionId}`;
        const { data } = await api.get(path);
        setPlanInfo(data);

        if (data.payment_status === "paid") {
          setStatus("success");
          if (!isV2) {
            try { await api.post("/pro/provision"); } catch { /* ignore */ }
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch {
        // continue polling
      }

      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, pollInterval);
      } else {
        setStatus("timeout");
      }
    };

    poll();
  }, [sessionId, purchaseType]);

  if (status === "polling") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="payment-polling">
        <div className="text-center space-y-4">
          <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-zinc-500">Verifying payment...</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    // v2 flow: customize success per purchase_type
    if (purchaseType) {
      const content = {
        verification: {
          title: "Account verified",
          subtitle: "Your receipt will arrive in this inbox shortly.",
          badge: "VERIFIED",
          badgeCls: "bg-emerald-50 text-emerald-700 border-emerald-200",
        },
        day_pass: {
          title: "Day Pass active",
          subtitle: "You have full access for the next 24 hours. Receipt is on its way to this inbox.",
          badge: "DAY PASS",
          badgeCls: "bg-amber-50 text-amber-700 border-amber-200",
        },
        pro_monthly: {
          title: "Pro Monthly active",
          subtitle: "Welcome to QuickInbox Pro. Your receipt will arrive in this inbox.",
          badge: "PRO MONTHLY",
          badgeCls: "bg-blue-50 text-blue-600 border-blue-200",
        },
        pro_yearly: {
          title: "Pro Yearly active",
          subtitle: "Welcome to QuickInbox Pro. Your receipt will arrive in this inbox.",
          badge: "PRO YEARLY",
          badgeCls: "bg-blue-50 text-blue-600 border-blue-200",
        },
      }[purchaseType] || {};
      return (
        <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="payment-success">
          <div className="max-w-sm text-center space-y-6">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-green-50 flex items-center justify-center">
              <PartyPopper size={32} className="text-green-600" />
            </div>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-[#111111]">{content.title}</h1>
              <p className="mt-2 text-sm text-zinc-500">{content.subtitle}</p>
            </div>
            {content.badge && (
              <div className="flex justify-center">
                <Badge variant="outline" className={`${content.badgeCls} text-xs font-mono`} data-testid="payment-success-badge">{content.badge}</Badge>
              </div>
            )}
            <Button
              onClick={() => navigate(`/inbox?upgrade=success&type=${purchaseType}`)}
              className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="payment-success-continue"
            >
              Open your inbox <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      );
    }

    const intervalLabel = planInfo?.interval === "yearly" ? "Yearly" : "Monthly";

    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="payment-success">
        <div className="max-w-sm text-center space-y-6">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-green-50 flex items-center justify-center">
            <PartyPopper size={32} className="text-green-600" />
          </div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-[#111111]">
              Subscription active!
            </h1>
            <p className="mt-2 text-sm text-zinc-500">
              Welcome to QuickInbox Pro. All premium features are now unlocked.
            </p>
          </div>

          <div className="flex justify-center gap-2">
            <Badge className="bg-blue-50 text-blue-600 border-blue-200 text-xs font-mono">PRO</Badge>
            <Badge variant="outline" className="text-xs font-mono text-zinc-500">
              <Calendar size={11} className="mr-1" /> {intervalLabel}
            </Badge>
          </div>

          <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-left space-y-2">
            {["Unlimited aliases", "Categories & VIP", "Batch actions", "Email forwarding", "Priority support"].map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm text-green-800">
                <Check size={14} className="text-green-600" /> {f}
              </div>
            ))}
          </div>
          <Button
            onClick={() => navigate("/pro/dashboard")}
            className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="payment-success-pro-setup"
          >
            Set up your PRO account <ArrowRight size={16} className="ml-2" />
          </Button>
          <Button
            onClick={() => navigate("/inbox")}
            variant="outline"
            className="w-full h-11"
            data-testid="payment-success-dashboard"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Expired or timeout
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="payment-failed">
      <div className="max-w-sm text-center space-y-5">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-[#F5F5F5] flex items-center justify-center">
          <Mail size={28} className="text-zinc-500" />
        </div>
        <h1 className="font-heading text-xl font-medium text-[#111111]">
          {status === "expired" ? "Session expired" : "Payment verification pending"}
        </h1>
        <p className="text-sm text-zinc-500">
          {status === "expired"
            ? "Your payment session has expired. Please try again."
            : "We couldn't verify your payment yet. It may take a moment."}
        </p>
        <div className="flex flex-col gap-2">
          <Button onClick={() => navigate("/inbox")} className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white">
            Go to Dashboard
          </Button>
          <Button onClick={() => navigate("/pricing")} variant="outline" className="w-full h-10">
            Try again
          </Button>
        </div>
      </div>
    </div>
  );
}
